/** The numbers a link writes, in the order a question names them. */
export const LINKED_VALUE_KEYS = ['hp', 'ac', 'initBonus', 'resources'];
/** The numbers typed on an unlinked token that a link would replace; none on a token already linked. */
export function handEntered(o) {
    if (o.entry || o.character)
        return [];
    return LINKED_VALUE_KEYS.filter((k) => (k === 'resources' ? Object.keys(o.values.resources ?? {}).length > 0 : o.values[k] !== undefined));
}
/** The server's refusal of a link that would replace typed numbers, when it was not told to. */
export const REPLACE_ASKED = 'This replaces the numbers typed on this token. Link it again and choose Replace to replace them.';
const FUNCTIONS = {
    floor: (a) => Math.floor(a), ceil: (a) => Math.ceil(a), round: (a) => Math.round(a), abs: (a) => Math.abs(a),
    min: (...a) => Math.min(...a), max: (...a) => Math.max(...a),
};
const TOKEN = /\s*(\d+(?:\.\d+)?|[A-Za-z_][A-Za-z0-9_]*|[()+\-*/,])/y;
/** A score's modifier by the family's formula, or null when the formula is not in the grammar (or not a formula). */
export function modifierFormula(source) {
    if (typeof source !== 'string' || !source.trim() || source.length > 200)
        return null;
    const tokens = [];
    let at = 0;
    while (at < source.length) {
        TOKEN.lastIndex = at;
        const m = TOKEN.exec(source);
        if (!m) {
            if (/^\s*$/.test(source.slice(at)))
                break;
            return null;
        }
        tokens.push(m[1]);
        at = TOKEN.lastIndex;
    }
    let i = 0;
    const fail = () => { throw new SyntaxError('formula'); };
    const expr = () => {
        let node = term();
        while (tokens[i] === '+' || tokens[i] === '-') {
            const op = tokens[i++];
            const l = node;
            const r = term();
            node = op === '+' ? (v) => l(v) + r(v) : (v) => l(v) - r(v);
        }
        return node;
    };
    const term = () => {
        let node = unary();
        while (tokens[i] === '*' || tokens[i] === '/') {
            const op = tokens[i++];
            const l = node;
            const r = unary();
            node = op === '*' ? (v) => l(v) * r(v) : (v) => l(v) / r(v);
        }
        return node;
    };
    const unary = () => {
        if (tokens[i] === '-') {
            i++;
            const o = unary();
            return (v) => -o(v);
        }
        return primary();
    };
    const primary = () => {
        const t = tokens[i] ?? fail();
        if (t === '(') {
            i++;
            const n = expr();
            if (tokens[i++] !== ')')
                fail();
            return n;
        }
        if (/^\d/.test(t)) {
            i++;
            const n = Number(t);
            return () => n;
        }
        if (t === 'value') {
            i++;
            return (v) => v;
        }
        const fn = FUNCTIONS[t];
        if (!fn || tokens[i + 1] !== '(')
            return fail();
        i += 2;
        const args = [expr()];
        while (tokens[i] === ',') {
            i++;
            args.push(expr());
        }
        if (tokens[i++] !== ')')
            fail();
        return (v) => fn(...args.map((a) => a(v)));
    };
    try {
        const node = expr();
        if (i !== tokens.length)
            return null;
        return (score) => { const n = node(score); return Number.isFinite(n) ? Math.trunc(n) : null; };
    }
    catch {
        return null;
    }
}
export const ABILITY_CHECK_LIMITS = { count: 12, modifier: 1000 };
/**
 * A creature's ability checks from its stat block: each attribute of the family's scored group (the one whose registry
 * group carries a formula) that the entry holds as a number, with the modifier the formula makes of it.
 */
export function abilityChecks(group, fields) {
    const mod = group ? modifierFormula(group.formula) : null;
    if (!group || !mod)
        return [];
    const out = [];
    for (const f of group.fields.slice(0, ABILITY_CHECK_LIMITS.count)) {
        const score = fields[f.key];
        if (typeof score !== 'number' || !Number.isInteger(score))
            continue;
        const modifier = mod(score);
        if (modifier !== null && Math.abs(modifier) <= ABILITY_CHECK_LIMITS.modifier)
            out.push({ key: f.key, label: f.label, score, modifier });
    }
    return out;
}
//# sourceMappingURL=linking.js.map